import FetchBeneficiary from '@Application/Beneficiaries/Beneficiaries/Queries/FetchBeneficiary';
import fetchBeneficiary from '@Application/Beneficiaries/Beneficiaries/fetchBeneficiary';
import FetchSubject from '@Application/Subjects/Queries/FetchSubject';
import fetchSubject from '@Application/Subjects/fetchSubject';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import * as React from 'react';
import { useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';

export type SubjectModifiability = {
	/** `false` while the dossier is not loaded (or is another one, or its fetch failed). */
	readonly isKnown: boolean;
	/** `true` only for a loaded dossier the user may write to: a write action never shows on a guess. */
	readonly canModify: boolean;
	readonly isArchived: boolean;
	readonly displayName: string | null;
	readonly error: Error | null;
	/** Re-runs the active query (beneficiary or generic dossier). */
	readonly refetch: () => void;
};

/**
 * Whether the dossier still accepts write gestures. Reads — and following a run already in flight —
 * never depend on it.
 *
 * A generic dossier is known only if its type is the one the route names (`typeKey`).
 *
 * Both queries are always declared (rules of hooks); the one that does not match the address is
 * neutralised by a `stopOn` that is always true, so it never fetches.
 */
const useSubjectModifiability = (subject: SubjectAddress): SubjectModifiability => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const { isBeneficiary, subjectId, typeKey } = subject;
	// Read, never fetched: the route's guard has already loaded the types before a dossier screen opens.
	const types = useSelector((state) => state.subjectTypesStore.types);

	const {
		data: beneficiary,
		error: beneficiaryError,
		refetch: refetchBeneficiary,
	} = useStoreQuery({
		selector: (state) => state.beneficiariesStore.beneficiary,
		stopOn: (value) => !isBeneficiary || (value !== null && value.id === subjectId),
		query: () =>
			isBeneficiary
				? dispatch(fetchBeneficiary(new FetchBeneficiary(facilityId, subjectId)))
				: Promise.resolve(),
		deps: [facilityId, subjectId, isBeneficiary],
	});

	const {
		data: loadedSubject,
		error: subjectError,
		refetch: refetchSubject,
	} = useStoreQuery({
		selector: (state) => state.subjectsStore.subject,
		stopOn: (value) => isBeneficiary || (value !== null && value.id === subjectId),
		query: () =>
			isBeneficiary
				? Promise.resolve()
				: dispatch(fetchSubject(new FetchSubject(facilityId, subjectId))),
		deps: [facilityId, subjectId, isBeneficiary],
	});

	return React.useMemo(() => {
		if (isBeneficiary) {
			const isKnown = beneficiary !== null && beneficiary.id === subjectId;

			return {
				isKnown,
				canModify: isKnown && !beneficiary.isArchived,
				isArchived: isKnown && beneficiary.isArchived,
				displayName: isKnown ? `${beneficiary.lastName} ${beneficiary.firstName}` : null,
				error: beneficiaryError,
				refetch: refetchBeneficiary,
			};
		}

		// A dossier of another type than the route's is unknown, as if the server had answered 404.
		const isKnown =
			loadedSubject !== null &&
			loadedSubject.id === subjectId &&
			(types === null || types.findByKey(typeKey)?.id === loadedSubject.typeId);

		return {
			isKnown,
			canModify: isKnown && loadedSubject.canModify,
			isArchived: isKnown && loadedSubject.isArchived,
			displayName: isKnown ? loadedSubject.title : null,
			error: subjectError,
			refetch: refetchSubject,
		};
	}, [
		isBeneficiary,
		subjectId,
		types,
		typeKey,
		beneficiary,
		loadedSubject,
		beneficiaryError,
		subjectError,
		refetchBeneficiary,
		refetchSubject,
	]);
};

export default useSubjectModifiability;
